import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Container,
    Grid,
    InputAdornment,
    Link,
    Modal,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import Layout from '../../Layout';
import { toast } from 'react-toastify';
import api from '../../api';
import { useNavigate } from 'react-router-dom';

import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';

/* ----------------------------- Styled elements ---------------------------- */

const Card = styled(Paper)(({ theme }) => ({
    borderRadius: 16,
    padding: theme.spacing(4),
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: `1px solid ${alpha('#000', 0.06)}`,
    boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
}));

const Field = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 10,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
    },
}));

/* -------------------------------- Helpers -------------------------------- */

const getLabel = (opt) => {
    if (typeof opt === 'string') return opt;
    if (!opt) return '';
    return opt.label || opt.name || opt.value || String(opt);
};

const isEqualOpt = (option, value) => getLabel(option) === getLabel(value);

const normalizeToStrings = (arr) =>
    (arr || [])
        .map((x) =>
            typeof x === 'string' ? x : x?.label || x?.name || x?.value || String(x)
        )
        .filter(Boolean);

const todayISO = () => new Date().toISOString().slice(0, 10);

const endOfLastMonthISO = () => {
    const d = new Date();
    d.setDate(0); // last day of previous month
    return d.toISOString().slice(0, 10);
};

const endOfLastQuarterISO = () => {
    const d = new Date();
    const month = d.getMonth(); // 0-based
    const quarter = Math.floor(month / 3); // 0,1,2,3
    const lastQuarterEndMonth = quarter * 3 - 1; // end of previous quarter
    if (lastQuarterEndMonth < 0) {
        d.setFullYear(d.getFullYear() - 1);
        d.setMonth(11); // December
    } else {
        d.setMonth(lastQuarterEndMonth + 1, 0); // monthIndex+1, day=0 => last day previous month
    }
    return d.toISOString().slice(0, 10);
};

/* --------------------------- Main Component ------------------------------- */

const AgeingReportPage = () => {
    const navigate = useNavigate();

    const [selections, setSelections] = useState({
        company: '',
        location: [],
        endDate: '',
    });

    const [companies, setCompanies] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);

    const [loadingMeta, setLoadingMeta] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [submittedOnce, setSubmittedOnce] = useState(false);

    /* ----------------------------- Validation ------------------------------ */

    const errors = useMemo(() => {
        const e = {};
        if (!selections.company) e.company = 'Company is required';
        if (!selections.endDate) e.endDate = 'As-of date is required';
        return e;
    }, [selections]);

    const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);
    const singleCompany = companies.length === 1;

    /* ----------------------------- Load metadata --------------------------- */
    useEffect(() => {
        const fetchMeta = async () => {
            try {
                setLoadingMeta(true);
                const { data } = await api.get('detail/userinfo?view=meta');

                const companyName = data?.companyDetails?.company_name || '';
                const companyNames = companyName ? [companyName] : [];
                setCompanies(normalizeToStrings(companyNames));

                // locations: string[][]
                const locArrays = Array.isArray(data?.companyDetails?.locations)
                    ? data.companyDetails.locations.filter(Array.isArray)
                    : [];

                const allLocations = locArrays.flat();
                const uniqueLocations = Array.from(
                    new Set(normalizeToStrings(allLocations))
                );
                setAvailableLocations(uniqueLocations);

                if (!companyName) {
                    toast.error('No company information found for this user.');
                }

                // Try restore filters from localStorage
                const savedRaw = localStorage.getItem('ageingReportFilters');
                let endDate = todayISO();
                let location = [];

                if (savedRaw) {
                    try {
                        const saved = JSON.parse(savedRaw);
                        if (saved.endDate) endDate = saved.endDate;
                        if (Array.isArray(saved.location)) location = saved.location;
                    } catch {
                        // ignore parse error
                    }
                }

                setSelections({
                    company: companyName || '',
                    location,
                    endDate,
                });
            } catch (error) {
                console.error('Error fetching user/company info:', error);
                if (error?.response?.status === 401) setSessionModalOpen(true);
                toast.error(error?.response?.data?.error || 'Failed to load options.');
            } finally {
                setLoadingMeta(false);
            }
        };

        fetchMeta();
    }, []);

    /* -------------- Persist selections (localStorage) ---------------------- */
    useEffect(() => {
        if (!selections.company) return;
        const payload = {
            company: selections.company,
            location: selections.location,
            endDate: selections.endDate,
        };
        localStorage.setItem('ageingReportFilters', JSON.stringify(payload));
    }, [selections.company, selections.location, selections.endDate]);

    /* ----------------------- Location options ------------------------------ */

    const locationOptions = useMemo(
        () => ['All', ...normalizeToStrings(availableLocations)],
        [availableLocations]
    );

    const handleCompanyChange = (_, value) => {
        const val = value || '';
        setSelections((prev) => ({
            ...prev,
            company: val,
            location: [],
        }));
    };

    const handleLocationChange = (_, values) => {
        const final = values.includes('All')
            ? ['All']
            : normalizeToStrings(values.filter((v) => v !== 'All'));
        setSelections((prev) => ({ ...prev, location: final }));
    };

    const handleEndDateChange = (e) => {
        setSelections((prev) => ({ ...prev, endDate: e.target.value }));
    };

    const handleSetEndDate = (dateISO) => {
        setSelections((prev) => ({ ...prev, endDate: dateISO }));
    };

    const handleReset = () => {
        setSelections({
            company: singleCompany ? companies[0] || '' : '',
            location: [],
            endDate: todayISO(),
        });
        setSubmittedOnce(false);
    };

    const handleGenerateReport = async () => {
        setSubmittedOnce(true);
        if (!isValid) {
            toast.error(
                `Missing required fields: ${Object.values(errors)
                    .map((x) => x.replace(' is required', ''))
                    .join(', ')}`
            );
            return;
        }

        const { company, location, endDate } = selections;
        const locationsToSend =
            location && location.length ? location : ['All']; // treat empty as All

        try {
            setSubmitting(true);
            const response = await api.post('reports/ageing-trackers', {
                company,
                location: locationsToSend,
                endDate,
            });

            if (response.status === 200) {
                navigate('/compliance-aging-report', {
                    state: {
                        reportData: response.data,
                        Company: company,
                        enddate: endDate,
                        Location: locationsToSend,
                    },
                });
                toast.success('Report generated successfully!');
            } else {
                toast.error('Failed to generate the report. Please try again.');
            }
        } catch (error) {
            console.error('Error generating report:', error);
            if (error?.response?.status === 401) setSessionModalOpen(true);
            toast.error(error?.response?.data?.error || 'Something went wrong.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSessionModalClose = () => {
        setSessionModalOpen(false);
        navigate('/login');
    };

    /* --------------------------- Filter summary chips ---------------------- */

    const filterSummaryChips = useMemo(() => {
        const chips = [];

        if (selections.company) {
            chips.push({
                label: `Company: ${selections.company}`,
                key: 'company',
            });
        }

        if (selections.location.includes('All')) {
            chips.push({
                label: 'Locations: All company locations',
                key: 'location-all',
            });
        } else if (selections.location.length) {
            chips.push({
                label: `Locations: ${selections.location.join(', ')}`,
                key: 'location',
            });
        } else {
            chips.push({
                label: 'Locations: All company locations (default)',
                key: 'location-default',
            });
        }

        if (selections.endDate) {
            chips.push({
                label: `As of: ${selections.endDate}`,
                key: 'endDate',
            });
        }

        return chips;
    }, [selections]);

    /* --------------------------------- Render ------------------------------ */

    return (
        <Layout title="Ageing Report">
            {/* Session expired modal */}
            <Modal
                open={sessionModalOpen}
                onClose={handleSessionModalClose}
                aria-labelledby="session-expired-modal-title"
                aria-describedby="session-expired-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 420,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        id="session-expired-modal-title"
                        variant="h6"
                        sx={{ mb: 1.5, fontWeight: 700, color: 'error.main' }}
                    >
                        Session Expired
                    </Typography>
                    <Typography
                        id="session-expired-modal-description"
                        sx={{ mb: 3, color: 'text.secondary' }}
                    >
                        Your session has expired. Please log in again to continue.
                    </Typography>
                    <Button variant="contained" onClick={handleSessionModalClose}>
                        Go to Login
                    </Button>
                </Box>
            </Modal>

            {/* Page background */}
            <Box
                sx={{
                    minHeight: '100vh',
                    background:
                        'linear-gradient(180deg, #f5f7fb 0%, #ffffff 40%, #f7fafc 100%)',
                    py: { xs: 3, md: 6 },
                }}
            >
                <Container maxWidth="lg">
                    {/* Breadcrumbs */}
                    <Breadcrumbs sx={{ mb: 2, color: 'text.secondary' }}>
                        <Link underline="hover" color="inherit" href="#">
                            Reports
                        </Link>
                        <Typography color="text.primary">Ageing Report</Typography>
                    </Breadcrumbs>

                    <Card elevation={0}>
                        <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 800,
                                    color: '#1f2937',
                                    letterSpacing: 0.3,
                                    textAlign: 'center',
                                }}
                            >
                                Ageing Report
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                textAlign="center"
                            >
                                View non-complied trackers for your company up to a selected
                                date. Locations are optional and default to all company
                                locations.
                            </Typography>
                        </Stack>

                        {loadingMeta ? (
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{ py: 6 }}
                            >
                                <CircularProgress />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 2 }}
                                >
                                    Loading options…
                                </Typography>
                            </Stack>
                        ) : (
                            <>
                                {/* Filters grid */}
                                <Grid container spacing={3} alignItems="center">
                                    {/* Company */}
                                    <Grid item xs={12} md={4}>
                                        <Autocomplete
                                            options={companies}
                                            value={selections.company}
                                            onChange={handleCompanyChange}
                                            getOptionLabel={getLabel}
                                            isOptionEqualToValue={isEqualOpt}
                                            disabled={singleCompany}
                                            renderInput={(params) => (
                                                <Field
                                                    {...params}
                                                    label="Company"
                                                    placeholder="Select company"
                                                    error={submittedOnce && !!errors.company}
                                                    helperText={
                                                        submittedOnce && errors.company
                                                            ? errors.company
                                                            : singleCompany
                                                                ? 'Auto-selected from your profile'
                                                                : ''
                                                    }
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <BusinessIcon fontSize="small" />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    size="medium"
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Location (multi) */}
                                    <Grid item xs={12} md={4}>
                                        <Autocomplete
                                            multiple
                                            options={locationOptions}
                                            value={
                                                selections.location.includes('All')
                                                    ? ['All']
                                                    : selections.location
                                            }
                                            onChange={handleLocationChange}
                                            getOptionLabel={getLabel}
                                            isOptionEqualToValue={isEqualOpt}
                                            disableCloseOnSelect
                                            disabled={!selections.company}
                                            renderInput={(params) => (
                                                <Field
                                                    {...params}
                                                    label="Location"
                                                    placeholder={
                                                        !availableLocations.length
                                                            ? 'No locations configured (all will be considered)'
                                                            : 'Select location(s) or leave empty for all'
                                                    }
                                                    helperText={
                                                        selections.location.includes('All') ||
                                                            !selections.location.length
                                                            ? 'All company locations will be included'
                                                            : 'Choose “All” to select every location'
                                                    }
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <LocationOnIcon fontSize="small" />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    size="medium"
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* As-of Date */}
                                    <Grid item xs={12} md={4}>
                                        <Field
                                            label="As-of Date"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={selections.endDate}
                                            onChange={handleEndDateChange}
                                            error={submittedOnce && !!errors.endDate}
                                            helperText={
                                                submittedOnce && errors.endDate
                                                    ? errors.endDate
                                                    : 'Includes trackers with due dates up to this date'
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EventIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            <Chip
                                                size="small"
                                                label="Today"
                                                clickable
                                                onClick={() => handleSetEndDate(todayISO())}
                                                variant="outlined"
                                            />
                                            <Chip
                                                size="small"
                                                label="End of last month"
                                                clickable
                                                onClick={() => handleSetEndDate(endOfLastMonthISO())}
                                                variant="outlined"
                                            />
                                            <Chip
                                                size="small"
                                                label="End of last quarter"
                                                clickable
                                                onClick={() => handleSetEndDate(endOfLastQuarterISO())}
                                                variant="outlined"
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>

                                {/* Filter summary */}
                                {filterSummaryChips.length > 0 && (
                                    <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        spacing={1}
                                        sx={{ mt: 3 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mr: 1 }}
                                        >
                                            Current filters:
                                        </Typography>
                                        {filterSummaryChips.map((chip) => (
                                            <Chip
                                                key={chip.key}
                                                label={chip.label}
                                                size="small"
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                )}

                                {/* Actions */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    justifyContent="center"
                                    sx={{ mt: 4 }}
                                >
                                    <Tooltip
                                        title={!isValid ? 'Fill required fields to proceed' : ''}
                                    >
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={handleGenerateReport}
                                                disabled={!isValid || submitting}
                                                startIcon={<AssessmentIcon />}
                                                sx={{ px: 4, py: 1.4, borderRadius: 3 }}
                                            >
                                                {submitting ? 'Generating…' : 'Generate Report'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={handleReset}
                                        startIcon={<RestartAltIcon />}
                                        sx={{ px: 3, py: 1.3, borderRadius: 3 }}
                                    >
                                        Reset
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Card>
                </Container>
            </Box>
        </Layout>
    );
};

export default AgeingReportPage;

